import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import useBreakpoints from '@hooks/useBreakpoints';
import useTheme from '@cvent/blocks/hooks/useTheme';
import Flyout from '@components/shared/Flyout';
import { injectTestId } from '@cvent/nucleus-test-automation';
import { Col } from '@cvent/carina/components/Col';
import { Row } from '@cvent/carina/components/Row';
import { Placements } from '@cvent/carina/components/Popover';
import { useTranslate } from 'nucleus-text';
import { usePageActionsApi } from '@metrics/client/usePageActionsApi';
import { ENGAGEMENT_MESSAGE_TYPE, ENGAGEMENT_PANEL_IDS, NOTIFICATION_TYPE } from '@components/engagement/constants';
import useEngagementMessages from '@components/engagement/useEngagementMessages';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import useNavFlyoutPlacement from '@hooks/useNavFlyoutPlacement';
import { CSSObject } from '@emotion/react';
import { MoodThemeProvider } from '@components/blocks/BlockThemeProvider';
import { isMobile as isMobileDevice, isTablet as isTabletDevice } from 'react-device-detect';
import Icon from '@components/shared/Icon';
import { notificationPropType } from './notificationPropType';
import NotificationFlyout from './notificationFlyout/NotificationFlyout';
import NotificationAlert from './NotificationAlert';
import NotificationBadge from './NotificationBadge';

const LOG = LoggerFactory.create('NotificationNavigationSensor');

const useStyles = ({
  navHeight,
  isFlyoutVisible,
  unreadNotificationsCount
}: {
  navHeight?: number;
  isFlyoutVisible: boolean;
  unreadNotificationsCount: number;
}): Record<string, CSSObject> => {
  const { isMobile } = useBreakpoints();
  const { backgroundColor, font, block, size } = useTheme();
  const topPosition = navHeight - navHeight * 0.2; // Overlap the bottom 20% of the nav with the toast

  return {
    alertDisplayPosition: isMobile ? { bottom: 15, right: 15, left: 15 } : { top: topPosition, right: 15 },
    notificationButton: {
      borderRadius: 10,
      marginRight: !isMobile ? 0 : 12,
      paddingLeft: !isMobile ? 13 : 0,
      paddingRight: !isMobile ? 13 : 0,
      paddingTop: !isMobile ? 10 : 0,
      paddingBottom: !isMobile ? 9 : 0,
      marginTop: isMobile ? 4 : 0,
      position: 'relative',
      border: 'none',
      cursor: 'pointer',
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: !isFlyoutVisible && !isMobile ? backgroundColor.brand.hover : null
      },
      '&:focus': {
        backgroundColor: isFlyoutVisible && !isMobile ? backgroundColor.brand.focus : null,
        // imitating Chrome default focus outline in Firefox
        '@-moz-document url-prefix()': !isMobile
          ? {
              outline: '2px solid blue',
              boxShadow: '0 0 0 3px white'
            }
          : null
      }
    },
    unreadBadge: {
      width: 10,
      height: 10,
      borderRadius: '50%',
      background: backgroundColor.danger.fill.base,
      position: 'absolute',
      left: 16,
      bottom: 23
    },
    unreadCountBadge: {
      position: 'absolute',
      left: '16px',
      top: '-5px',
      border: unreadNotificationsCount ? `2px solid ${block.base}` : 'none',
      borderRadius: size.border.radius.round
    },
    notificationIconContainer: {
      marginTop: !isMobile ? 3 : 0
    },
    mobileTitle: {
      fontSize: font.base.size.m,
      color: font.color.base,
      paddingLeft: 12
    },
    activeButton: {
      backgroundColor: backgroundColor.brand.hover
    },
    activeBadge: {
      borderColor: backgroundColor.brand.hover
    }
  };
};

/**
 * Filters notifications to be displayed
 * @param {*} notifications
 * @returns
 */
const filterNotificationsToBeDisplayed = notifications => {
  const pushNotifications = notifications.filter(notif => notif.type === NOTIFICATION_TYPE.PUSH);
  // if no push was received no banner will be displayed
  // if one push was received, banner will be displayed for the push
  // if more than one push was received total count will be displayed
  return pushNotifications.length <= 1 ? pushNotifications : notifications;
};

/**
 * Component that renders in-app notification banner and notification flyout
 * handles visibility banner to flyout on click of banner
 */
function NotificationNavigationSensor(props) {
  const {
    notificationList,
    eventId,
    allowAlertNotification,
    notificationsLoading,
    markAndPublishNotificationsAsRead,
    toastRedesignEnabled = false,
    navHeight,
    navHeaderVisible,
    surveyUrl
  } = props;
  const { translate } = useTranslate();
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [isFlyoutVisible, setIsFlyoutVisible] = useState(false);
  const [lastTrackedNotificationTime, setLastTrackedNotificationTime] = useState(new Date());
  const [newNotifications, setNewNotifications] = useState([]);
  const { font, size } = useTheme();
  const { isMobile } = useBreakpoints();
  const notificationIconRef = useRef(null);

  const { notificationClicked } = usePageActionsApi();
  const { notifyMessage } = useEngagementMessages();
  const showUnreadBadge = notificationList.some(notification => !notification.isRead);
  const unreadNotificationsCount = notificationList.reduce(
    (unReadCount, notification) => unReadCount + !notification.isRead,
    0
  );
  const styles = useStyles({
    navHeight,
    isFlyoutVisible,
    unreadNotificationsCount
  });
  // align right edge of flyout to right edge of profile icon (desktop) or menu icon (tablet)
  const placementOffset = useNavFlyoutPlacement(
    notificationIconRef?.current?.getBoundingClientRect()?.right || 0,
    isFlyoutVisible
  );

  /**
   * method called on clicking X or Dismiss button on in-app notification banner
   */
  const onAlertDismiss = useCallback(() => {
    setIsAlertVisible(false);
  }, [setIsAlertVisible]);

  const trackNotification = useCallback(
    notification => {
      const { id, linkedContent: linkedContentToBeTracked } = notification;
      const hasLinkedContent = linkedContentToBeTracked !== undefined;
      const linkedContentType =
        linkedContentToBeTracked?.contentType === 'LinkedExternalPage'
          ? 'LinkedPage'
          : linkedContentToBeTracked?.contentType;
      notificationClicked({
        notificationId: id,
        hasLinkedContent,
        contentType: hasLinkedContent ? linkedContentType : null,
        content: hasLinkedContent ? linkedContentToBeTracked.content : null,
        isClickedFromPush: false
      });
    },
    [notificationClicked]
  );
  /**
   * method that handles visibility of alert and flyout on click of in-app alert banner
   */
  const onAlertClick = useCallback(
    notifications => {
      setIsAlertVisible(false);
      const notificationIds = [];
      if (notifications.length === 1 && notifications[0]?.linkedContent && !notifications[0]?.shouldSkipReadForAlert) {
        const notification = notifications[0];
        trackNotification(notification);
        notificationIds.push(notification.id);
        markAndPublishNotificationsAsRead(notificationIds);
        return;
      }
      setIsFlyoutVisible(true);
      notifications.forEach(notification => {
        const { linkedContent: linkedContentToBeTracked } = notification;
        const hasLinkedContent = linkedContentToBeTracked !== undefined;
        if (!hasLinkedContent) {
          trackNotification(notification);
          notificationIds.push(notification.id);
        }
      });
      markAndPublishNotificationsAsRead(notificationIds);
    },
    [markAndPublishNotificationsAsRead, trackNotification]
  );

  // sets last received notification time when alert is hidden to track new notifications
  useEffect(() => {
    if (!isAlertVisible && notificationList.length) {
      setLastTrackedNotificationTime(notificationList[0].publishedAt);
    }
    // do not add notificationList to dependency list, otherwise when alert is not visible
    // and new notification is received, it won't be displayed in alert
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAlertVisible]);

  // setting new notifications array for alert
  useEffect(() => {
    if (notificationList.length) {
      if (!isFlyoutVisible && allowAlertNotification) {
        const index = notificationList.findIndex(
          notification => notification.publishedAt <= lastTrackedNotificationTime
        );

        const notificationsToBeDisplayed =
          index === -1 ? [...notificationList] : filterNotificationsToBeDisplayed(notificationList.slice(0, index));
        setNewNotifications(notificationsToBeDisplayed);
        if (notificationsToBeDisplayed.length) {
          setIsAlertVisible(true);

          if (allowAlertNotification && toastRedesignEnabled && !isFlyoutVisible) {
            LOG.debug('Displaying planner notifications as toasts', notificationsToBeDisplayed);
            notifyMessage(ENGAGEMENT_PANEL_IDS.PLANNER_NOTIFICATIONS, {
              msgType: ENGAGEMENT_MESSAGE_TYPE.TOAST,
              data: notificationsToBeDisplayed,
              onDismiss: () => {
                onAlertDismiss();
              },
              onAlertClick: () => {
                onAlertClick(notificationsToBeDisplayed);
              }
            });
            setLastTrackedNotificationTime(notificationList[0].publishedAt);
          }
        }
      } else {
        setLastTrackedNotificationTime(notificationList[0].publishedAt);
      }
    }
  }, [
    notificationList,
    lastTrackedNotificationTime,
    isFlyoutVisible,
    allowAlertNotification,
    toastRedesignEnabled,
    notifyMessage,
    onAlertDismiss,
    onAlertClick
  ]);

  const notificationFlyout = (
    <Flyout
      triggerMethod={isMobileDevice || isTabletDevice ? 'CLICK' : 'HOVER'}
      trigger={({ toggleOpen, isVisible }) => (
        <button
          type="button"
          ref={notificationIconRef}
          onClick={() => {
            toggleOpen();
          }}
          css={[styles.notificationButton, isVisible ? styles.activeButton : null]}
          {...injectTestId('notification-icon')}
          aria-label={`${translate('Notifications-Bell-Icon')}${
            unreadNotificationsCount > 0
              ? ` - ${translate('Unread-Notifications', {
                  count: unreadNotificationsCount
                })}`
              : ''
          }`}
          aria-expanded={isVisible}
        >
          <Row>
            <Col padding={0} width="content">
              {!notificationsLoading && showUnreadBadge && isMobile ? (
                <div css={styles.unreadBadge} {...injectTestId('unread-badge-dot')} />
              ) : (
                <div css={[styles.unreadCountBadge, isFlyoutVisible ? { ...styles.activeBadge } : null]}>
                  <NotificationBadge
                    unreadNotificationsCount={unreadNotificationsCount}
                    isNotificationsLoading={notificationsLoading}
                  />
                </div>
              )}
              <div css={styles.notificationIconContainer}>
                <Icon name="bell" size="md" color={font.color.base} aria-hidden />
              </div>
            </Col>
            <Col
              padding={0}
              width="content"
              flex={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {isMobile ? <div css={styles.mobileTitle}>{translate('Notifications-Flyout-Header-Title')}</div> : null}
            </Col>
          </Row>
        </button>
      )}
      placement={Placements.bottomEnd}
      placementOffset={{ left: placementOffset, top: 16 }}
      isVisible={isFlyoutVisible}
      toggleVisible={setIsFlyoutVisible}
      zIndex={1000}
      hasSpacerAfterTrigger
    >
      {isFlyoutVisible
        ? ({ toggleOpen }) => (
            <MoodThemeProvider css={{ borderRadius: size.border.radius.fiveX }}>
              <NotificationFlyout
                toggleOpen={() => {
                  toggleOpen();
                }}
                content={notificationList}
                eventId={eventId}
                notificationsLoading={notificationsLoading}
                markAndPublishNotificationsAsRead={markAndPublishNotificationsAsRead}
                navHeaderVisible={navHeaderVisible}
                surveyUrl={surveyUrl}
              />
            </MoodThemeProvider>
          )
        : null}
    </Flyout>
  );
  return (
    <>
      {allowAlertNotification &&
      !toastRedesignEnabled &&
      isAlertVisible &&
      !isFlyoutVisible &&
      newNotifications.length ? (
        <NotificationAlert
          displayPosition={styles.alertDisplayPosition}
          notificationsData={newNotifications}
          onDismiss={onAlertDismiss}
          onAlertClick={() => onAlertClick(newNotifications)}
          eventId={eventId}
          surveyUrl={surveyUrl}
        />
      ) : null}
      {notificationFlyout}
    </>
  );
}

NotificationNavigationSensor.propTypes = {
  /** List of new notifications received */
  notificationList: PropTypes.arrayOf(PropTypes.shape(notificationPropType)).isRequired,
  eventId: PropTypes.string,
  allowAlertNotification: PropTypes.bool,
  allowAlertAnnouncementNotification: PropTypes.bool,
  allowAlertAppointmentNotification: PropTypes.bool,
  updateAllowAlertNotification: PropTypes.func,
  updateNotificationSetting: PropTypes.func,
  notificationsLoading: PropTypes.bool,
  markAndPublishNotificationsAsRead: PropTypes.func,
  toastRedesignEnabled: PropTypes.bool,
  navHeight: PropTypes.number,
  navHeaderVisible: PropTypes.bool,
  surveyUrl: PropTypes.string
};

export default NotificationNavigationSensor;
